import { useState, FormEvent } from 'react';

export default function FinalCTA() {
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      await fetch('https://readdy.ai/api/form/d7734hqqo7glkto3e0og', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-[#050508] text-white py-32 px-6 md:px-12 relative overflow-hidden">
      {/* Grid texture */}
      <div className="absolute inset-0 opacity-20">
        <div className="w-full h-full" style={{
          backgroundImage: 'linear-gradient(rgba(0,255,180,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,180,0.04) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
      </div>

      {/* Decorative rings */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-[#00ffb4]/[0.04]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-[#00ffb4]/[0.06]" />

      <div className="relative z-10 max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-px w-12 bg-[#00ffb4]/20" />
            <span className="font-mono text-[11px] tracking-[0.3em] text-[#00ffb4]/60 uppercase">
              V4.0 Beta Access
            </span>
            <div className="h-px w-12 bg-[#00ffb4]/20" />
          </div>

          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 leading-tight">
            TWINODE 核心戰略報告
          </h2>

          <p className="text-base md:text-lg text-white/40 max-w-2xl mx-auto leading-relaxed">
            全自動化。確定性核心。5 分鐘內交付至您的信箱。
          </p>
        </div>

        {/* Report Card */}
        <div className="max-w-2xl mx-auto bg-[#0a0a0f] border border-[#00ffb4]/15 rounded-lg p-10 md:p-12">
          <div className="text-center mb-8">
            <div className="inline-block border border-[#00ffb4]/40 bg-[#00ffb4]/10 px-4 py-1.5 rounded-full text-xs tracking-[0.2em] text-[#00ffb4] mb-6 font-mono">
              BETA — 限量 50 席
            </div>

            <div className="mb-6">
              <span className="text-5xl md:text-6xl font-bold tracking-tight text-white">V4.0</span>
              <span className="text-xl text-white/30 ml-3 font-mono">BETA</span>
            </div>

            <p className="text-sm text-white/40 font-mono tracking-wide">
              申請邀請碼 → 進入加密終端機 → 獲取 PDF 戰略報告
            </p>
          </div>

          {/* What's Included */}
          <div className="border-t border-white/5 pt-8 mb-10">
            <h3 className="text-xs tracking-[0.25em] text-white/30 uppercase mb-6 font-mono">
              報告包含模組
            </h3>
            <div className="space-y-4">
              {[
                { title: '底層出廠設定解析', desc: '硬編碼物理特徵與基線元素映射' },
                { title: '情緒—職業合成矩陣', desc: '交叉引用行為模式與認知盲點識別' },
                { title: '歷史周期驗證', desc: '宏觀摩擦分析與環境時機校準' },
                { title: '2026 環境變量碰撞', desc: '時空概率收斂點計算' },
                { title: '流動性防禦協議', desc: '風險對沖策略與多維度防禦架構' },
                { title: '12 個月高頻預警圖', desc: '月度戰術行動協議與執行窗口建議' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-check-line text-[#00ffb4] text-lg"></i>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm mb-0.5">{item.title}</p>
                    <p className="text-xs text-white/30">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          <button
            onClick={() => setShowModal(true)}
            className="block w-full border border-[#00ffb4] text-[#00ffb4] hover:bg-[#00ffb4] hover:text-black text-center px-8 py-5 rounded-md text-sm font-bold transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            申請 V4.0 Beta 專屬邀請碼（限量 50 席）
          </button>

          <p className="text-xs text-white/20 text-center mt-6 font-mono tracking-wide">
            6 大矩陣 • &lt;5 分鐘交付 • 確定性核心演算
          </p>
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-white/30 max-w-xl mx-auto leading-relaxed">
            無訂閱。無追加銷售。一次申請，終身存取您的個人戰略防禦裝甲。
          </p>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={() => setShowModal(false)}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
          <div
            className="relative bg-[#0a0a0f] text-white rounded-lg max-w-lg w-full p-10 border border-[#00ffb4]/20 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-[#00ffb4]/50 rounded-tl-lg"></div>
            <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-[#00ffb4]/50 rounded-tr-lg"></div>
            <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 border-[#00ffb4]/50 rounded-bl-lg"></div>
            <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-[#00ffb4]/50 rounded-br-lg"></div>

            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {!submitted ? (
              <>
                <div className="mb-8">
                  <div className="inline-flex items-center gap-2 border border-[#00ffb4]/30 bg-[#00ffb4]/5 px-4 py-1.5 rounded-full text-xs tracking-widest text-[#00ffb4] mb-6 font-mono">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00ffb4] animate-pulse"></span>
                    BETA ACCESS — 限量 50 席
                  </div>
                  <h3 className="text-2xl font-bold mb-4 leading-tight">
                    申請 V4.0 Beta<br />
                    <span style={{ color: '#00ffb4' }}>專屬邀請碼</span>
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    提交您的 Email，系統將在 24 小時內發送一次性授權邀請碼（OTP）。
                  </p>
                </div>

                <form
                  id="final-cta-beta-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                >
                  <label className="text-xs tracking-widest text-gray-500 block mb-3 font-mono">
                    EMAIL 地址
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#00ffb4]/50 transition-colors mb-4"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full border border-[#00ffb4] text-[#00ffb4] hover:bg-[#00ffb4] hover:text-black px-6 py-3 rounded-md text-sm font-bold transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {submitting ? '提交中...' : '提交申請 → 獲取邀請碼'}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center mx-auto border border-[#00ffb4]/30 rounded-full mb-6">
                  <i className="ri-check-line text-3xl" style={{ color: '#00ffb4' }}></i>
                </div>
                <h3 className="text-xl font-bold mb-3">申請已接收</h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  邀請碼將在 24 小時內發送至您的信箱。
                </p>
                <div className="mt-6 font-mono text-xs text-gray-600">
                  STATUS: QUEUED — AWAITING AUTHORIZATION
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
